<template>
  <div>
    <div v-if="isLoaded" class="animate-fadeInUp">
      <div class="animate-fadeIn flex flex-col gap-12 px-24 py-16 lg:flex-row">
        <div v-if="!isLegacyMode" class="w-full lg:w-3/5">
          <h1>
            <span class="mt-2 text-3xl font-bold text-foreground">{{ $t(`pages.settings.setupPage.title`) }}</span>
          </h1>

          <p class="mb-12 text-lg text-muted-foreground">
            {{ $t(`pages.settings.setupPage.subtitle`) }}
          </p>

          <div
            v-if="
              formattedItems.filter(
                (subscription) => subscription.isSubscription === true && subscription.isSmsPack === false
              ).length > 0
            "
          >
            <h3 class="mb-6 text-lg font-semibold text-foreground">
              {{ $t(`pages.settings.setupPage.cartItems.subscriptions`) }}
            </h3>
            <CartItems
              :subscriptions="
                formattedItems.filter(
                  (subscription) => subscription.isSubscription === true && subscription.isSmsPack === false
                )
              "
            />
          </div>

          <div>
            <h3 class="mb-6 mt-12 text-lg font-semibold text-foreground">
              {{ $t(`pages.settings.setupPage.cartItems.sms`) }}
            </h3>
            <CartItems
              v-if="
                formattedItems.filter(
                  (subscription) => subscription.isSubscription === true && subscription.isSmsPack === true
                ).length > 0
              "
              :subscriptions="
                formattedItems.filter(
                  (subscription) => subscription.isSubscription === true && subscription.isSmsPack === true
                )
              "
            />
            <div class="group mb-4 flex flex-col justify-between rounded-lg border border-border bg-white">
              <div class="flex flex-col justify-start">
                <div class="p-5">
                  <div class="flex justify-between">
                    <div>
                      <p class="mb-1 flex items-center text-base font-semibold text-foreground">
                        {{ $t(`pages.settings.setupPage.cartItems.smsUnits`) }}
                      </p>
                      <p v-if="country !== null" class="mb-2 line-clamp-4 flex text-sm text-foreground">
                        <span> {{ $t(`pages.settings.setupPage.cartItems.smsCountry`) }} </span>
                        <img :src="country.flag" class="m-2 my-auto h-2 w-3" />{{ country.name }}
                      </p>
                    </div>
                    <div class="mb-4 flex flex-row items-center space-x-2">
                      <p class="rounded-md bg-primary/10 px-2 py-1.5 text-xs font-semibold leading-5 text-primary">
                        <span>
                          {{ sellingSmsPriceWithoutVat }}€ HT {{ $t(`pages.settings.subscriptionPage.monthly`) }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="formattedItems.filter((subscription) => subscription.isFees).length > 0">
            <h3 class="mb-6 mt-12 text-lg font-semibold text-foreground">
              {{ $t(`pages.settings.setupPage.cartItems.setup`) }}
            </h3>
            <CartItems :subscriptions="formattedItems.filter((subscription) => subscription.isFees)" />
          </div>
        </div>
        <div class="rounded-lg bg-muted p-12" :class="isLegacyMode ? 'w-1/2 m-auto' : 'w-full lg:w-2/5'">
          <div class="sticky top-10">
            <div class="flex gap-3 rounded-t-md border border-b border-border bg-white px-6 py-4 text-foreground">
              <BaseIcon name="HeroCheckBadgeIconSolid" class="w-6 text-green-500" />
              <p class="text-lg font-bold">{{ $t(`pages.settings.setupPage.subscribeBlock.title`) }}</p>
            </div>
            <div class="rounded-b-lg border border-border bg-white p-8">
              <p class="mb-12 text-sm font-semibold text-black">
                {{ $t(`pages.settings.setupPage.subscribeBlock.subtitle`) }}
              </p>
              <div class="flex flex-col sm:items-start">
                <label for="title" class="block text-sm font-medium text-foreground">
                  {{ $t(`pages.settings.setupPage.subscribeBlock.form.email.label`) }}
                </label>
                <div class="mt-1 w-full">
                  <AppFormInput
                    id="billingEmail"
                    v-model="billingEmail"
                    type="text"
                    name="billingEmail"
                    placeholder="ex : facturation@maboutique.com"
                  />
                </div>
                <span v-if="billingEmail && isValidEmail === false" class="mt-2 text-xs text-red-600">
                  {{ $t(`pages.settings.setupPage.subscribeBlock.form.email.errorMessage`) }}
                </span>
              </div>

              <div v-if="hasSiret" class="flex flex-col pt-6 sm:items-start">
                <label for="subtitle" class="block text-sm font-medium text-foreground">
                  {{ $t(`pages.settings.setupPage.subscribeBlock.form.siret.label`) }}
                </label>
                <div class="mt-1 w-full">
                  <AppFormInput
                    id="countryCompanyIdentifier"
                    v-model="countryCompanyIdentifier"
                    type="text"
                    name="countryCompanyIdentifier"
                    placeholder="ex : 83248676500023"
                    @input="fetchSiretData"
                  />
                  <div
                    v-if="countryCompanyIdentifier === null || countryCompanyIdentifier === ''"
                    class="mt-1 flex w-full items-center text-xs"
                  >
                    <AppCheckboxField
                      id="noSiret"
                      v-model="noSiret"
                      :label="$t(`pages.settings.setupPage.subscribeBlock.form.noSiret.label`)"
                    />
                  </div>
                </div>
                <div v-if="companyInfos && countryCompanyIdentifier !== ''">
                  <span class="text-xs text-green-600"
                    >{{ companyInfos.name }}, {{ companyInfos.address }}, {{ companyInfos.postal_code }},
                    {{ companyInfos.city }}</span
                  >
                </div>
              </div>

              <div v-else>
                <div class="flex flex-col pt-6 sm:items-start">
                  <label for="subtitle" class="block text-sm font-medium text-foreground">
                    {{ $t(`pages.settings.setupPage.subscribeBlock.form.vatIdentifier.label`) }}
                  </label>
                  <div class="mt-1 w-full">
                    <AppFormInput
                      id="vatIdentifier"
                      v-model="vatIdentifier"
                      type="text"
                      name="vatIdentifier"
                      placeholder="ex : FR46832486765"
                      @input="fetchSiretData"
                    />
                    <div
                      v-if="vatIdentifier === null || vatIdentifier === ''"
                      class="mt-1 flex w-full items-center text-xs"
                    >
                      <AppCheckboxField
                        id="hasSiret"
                        v-model="hasSiret"
                        :label="$t(`pages.settings.setupPage.subscribeBlock.form.hasSiret.label`)"
                      />
                    </div>
                  </div>
                </div>

                <div class="mt-8 flex flex-col sm:items-start">
                  <label for="title" class="block text-sm font-medium text-foreground">
                    {{ $t(`pages.settings.setupPage.subscribeBlock.form.companyName.label`) }}
                  </label>
                  <div class="mt-1 w-full">
                    <AppFormInput
                      id="companyName"
                      v-model="companyName"
                      type="text"
                      name="companyName"
                      placeholder="ex : HEY PONGO"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-8">
                <AppCheckboxField
                  id="optin"
                  v-model="optin"
                  :label="$t(`pages.settings.setupPage.subscribeBlock.form.confirm.label`)"
                />
              </div>

              <div class="mt-12 text-right">
                <Button
                  v-if="payout.withMandate"
                  variant="default-primary"
                  :disabled="!canAddMandate"
                  @click="addMandate"
                  >{{ $t(`pages.settings.setupPage.subscribeBlock.form.actions.subscribeAndPay`) }}</Button
                >
                <Button
                  v-else
                  variant="default-primary"
                  class="animate-beat"
                  :disabled="!canAddMandate"
                  @click="validateDataWithoutMandate"
                  >{{ $t(`pages.settings.setupPage.subscribeBlock.form.actions.subscribe`) }}</Button
                >
              </div>
            </div>
            <div class="mt-6 rounded-lg border border-border bg-white p-8">
              <div class="flex justify-between">
                <div class="isolate flex -space-x-2 overflow-hidden">
                  <img
                    class="relative z-30 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                    src="https://content.heypongo.com/produit/customers/spok2.png"
                    alt=""
                  />
                  <img
                    class="relative z-20 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                    src="https://content.heypongo.com/produit/customers/bigmoustache-min.png"
                    alt=""
                  />
                  <img
                    class="relative z-10 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                    src="https://content.heypongo.com/produit/customers/come-min.png"
                    alt=""
                  />
                  <img
                    class="relative z-0 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                    src="https://content.heypongo.com/produit/customers/sushisoba-min.png"
                    alt=""
                  />
                  <img
                    class="relative z-30 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                    src="https://content.heypongo.com/produit/customers/tontonsafros-min.png"
                    alt=""
                  />
                </div>
              </div>
              <div class="mt-4">
                <h4
                  class="text-sm font-medium text-foreground"
                  v-html="$t('pages.settings.setupPage.subscribeBlock.testimonials.title')"
                ></h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import { Button } from '@/ui/button'

import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import CartItems from './billing/Components/CartItems.vue'
import useLocalStorage from '@/composables/useLocalStorage'
import { canProceedWithMandateSetup, cleanVatIdentifier } from '@/utils/setupMandateValidation'

export default {
  components: {
    Button,
    TheLoadingPage,
    CartItems
  },
  setup() {
    const { user, setupToken } = useLocalStorage()
    const notification = useNotification()
    return {
      user,
      setupToken,
      notification
    }
  },
  data: () => {
    return {
      isLegacyMode: false, // Compatibility mode for onboarding links created before the new client onboarding flow with client_id. To be removed after 2025-03-10
      isLoaded: false,
      goCardlessFlowPage: false,
      payout: null,
      country: null,
      sellingSmsPriceWithoutVat: null,
      countryCompanyIdentifier: null,
      billingEmail: null,
      companyInfos: null,
      optin: false,
      noSiret: false,
      hasSiret: true,
      vatIdentifier: null,
      logoUrl: `${import.meta.env.VITE_AWS_STORAGE_URL}/logo.png`
    }
  },
  computed: {
    formattedItems() {
      const grouped = this.payout.client.distributions.reduce((acc, distribution) => {
        const unitPriceWithoutVat = distribution.store_company_product.unit_price_without_vat
        const productId = distribution.store_company_product.company_product.id
        const startedAtFormatted = distribution.store_company_product.started_at_formatted
        const slug = distribution.store_company_product.company_product.slug
        const totalPriceWithoutVat = distribution.store_company_product.total_price_without_vat
        const discountedPriceWithoutVat = distribution.store_company_product.discounted_price_without_vat
        const isSubscription = distribution.store_company_product.company_product.is_subscription
        const isTrial = distribution.store_company_product.is_trial
        const isSmsPack = distribution.store_company_product.sms_pack !== null
        const isFees = ['onboarding', 'setup'].includes(distribution.store_company_product.company_product.slug)
        const smsPackQuantity = isSmsPack ? distribution.store_company_product.sms_pack.included_quantity : null

        const startedAt = distribution.store_company_product.started_at
        const key = `${unitPriceWithoutVat}-${productId}-${startedAt}`

        if (!acc[key]) {
          acc[key] = {
            unitPriceWithoutVat: unitPriceWithoutVat,
            productId: productId,
            startedAtFormatted: startedAtFormatted,
            slug: slug,
            totalPriceWithoutVat: totalPriceWithoutVat,
            discountedPriceWithoutVat: discountedPriceWithoutVat,
            isSubscription: isSubscription,
            isTrial,
            isFees: isFees,
            isSmsPack: isSmsPack,
            smsPackQuantity: smsPackQuantity,
            distributions: []
          }
        }

        acc[key].distributions.push(distribution)

        return acc
      }, {})

      // Convert the result from an object to an array of objects
      return Object.values(grouped)
    },
    cleanedCountryCompanyIdentifier() {
      return this.countryCompanyIdentifier?.replace(/\s/g, '')
    },
    cleanedVatIdentifier() {
      return cleanVatIdentifier(this.vatIdentifier)
    },
    canAddMandate() {
      return canProceedWithMandateSetup({
        hasSiret: this.hasSiret,
        cleanedCountryCompanyIdentifier: this.cleanedCountryCompanyIdentifier,
        cleanedVatIdentifier: this.cleanedVatIdentifier,
        companyName: this.companyName,
        optin: this.optin,
        isValidEmail: this.isValidEmail
      })
    },
    isValidEmail() {
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      return regex.test(this.billingEmail)
    }
  },
  watch: {
    hasSiret(newVal) {
      if (this.noSiret === newVal) {
        this.noSiret = !newVal
      }
    },
    noSiret(newVal) {
      if (this.hasSiret === newVal) {
        this.hasSiret = !newVal
      }
    }
  },
  created() {
    if ('redirect_flow_id' in this.$route.query) {
      this.goCardlessFlowPage = true
    }
    this.axios.all([this.fetchPayout(), this.fetchSmsPrices()]).then(() => {
      this.isLoaded = true
    })
  },
  methods: {
    fetchPayout() {
      return this.axios.get(`/clients/payout/${this.setupToken}`).then((response) => {
        this.payout = response.data.data

        // Legacy mode for onboarding links created before the new client onboarding flow with client_id. To be removed after 2025-03-10
        if (!this.payout.client?.public_id) {
          this.isLegacyMode = true
          this.payout.client = {
            pennylane: {
              name: '',
              vat_number: ''
            },
            distributions: []
          }
        }

        this.countryCompanyIdentifier = this.payout.client.registration_number
        this.companyName = this.payout.client.pennylane.name
        this.vatIdentifier = this.payout.client.pennylane.vat_number

        if (this.countryCompanyIdentifier === null || this.countryCompanyIdentifier === '') {
          this.noSiret = true
        }

        this.fetchSiretData()
      })
    },
    fetchSmsPrices() {
      return this.axios.get(`/merchants/${this.user.merchant.id}/sms-prices`).then((response) => {
        this.merchantSmsPrices = response.data.data
        const price = this.merchantSmsPrices.filter((d) => d.country.id === 78)[0]
        this.sellingSmsPriceWithoutVat = price.sellingSmsPriceWithoutVat / 100
        this.country = price.country
      })
    },
    addMandate() {
      if (!this.canAddMandate) {
        return
      }
      this.axios
        .get('/settings/gocardless/begin', {
          params: {
            onboarding_token: this.setupToken,
            country_company_identifier: this.cleanedCountryCompanyIdentifier,
            vat_identifier: this.cleanedVatIdentifier,
            company_name: this.companyName,
            billing_email: this.billingEmail
          }
        })
        .then((response) => {
          window.location.href = response.data.data.redirect_url
        })
        .catch((error) => {
          this.notification.create('error', {
            text: error.response.data.message,
            timeout: 5000
          })
        })
    },
    validateDataWithoutMandate() {
      this.axios
        .put(`/clients/${this.payout.client.public_id}`, {
          country_company_identifier: this.cleanedCountryCompanyIdentifier,
          vat_identifier: this.cleanedVatIdentifier,
          company_name: this.companyName,
          billing_email: this.billingEmail
        })
        .then(() => {
          this.setupToken = ''
          this.$router.push({ name: 'Dashboard' })
        })
    },
    fetchSiretData() {
      if (this.cleanedCountryCompanyIdentifier?.length === 14) {
        return this.axios
          .get(`/siret/${this.cleanedCountryCompanyIdentifier}`)
          .then((response) => {
            this.companyInfos = response.data.data
          })
          .catch((error) => {
            this.notification.create('error', {
              text: error.response.data.message,
              timeout: 5000
            })
          })
      }
    }
  }
}
</script>
